import { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { LogOut, Settings, UserCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Logo } from '@/components/Logo';
import { useAuthStore } from '@/store/auth';
import { cognitoSignOut } from '@/lib/cognito';
import { cn, getInitials } from '@/lib/utils';

/**
 * Top-of-page header. Adapts visibly based on auth state:
 * - Logged out: regular nav + "Entrar / Começar grátis" CTAs.
 * - Logged in: subtle accent-tinted background, avatar with initials + name +
 *   colored role badge always visible (not hidden in a dropdown). Click avatar
 *   to open the user menu (Configurações, Sair).
 *
 * The goal here is to make the "I'm logged in as Lawyer/Cliente/Admin" state
 * unmistakable — both client feedback and admin feedback said the previous
 * header was too quiet about auth state.
 */
export function Header() {
  const user = useAuthStore((s) => s.user);
  const clearSession = useAuthStore((s) => s.clearSession);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close menu on click outside / Escape
  useEffect(() => {
    if (!menuOpen) return;
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  function handleLogout() {
    cognitoSignOut();
    clearSession();
    setMenuOpen(false);
    navigate('/');
  }

  const navItems = [
    { to: '/como-funciona', label: 'Como funciona' },
    { to: '/advogados', label: 'Advogados' },
    { to: '/templates', label: 'Templates' },
    { to: '/precos', label: 'Preços' },
    { to: '/faq', label: 'FAQ' },
  ];

  const roleHomePath = user
    ? user.role === 'lawyers'
      ? '/lawyer'
      : user.role === 'admins'
        ? '/admin'
        : '/app'
    : '/';

  const roleLabel = user
    ? user.role === 'lawyers'
      ? 'Advogado'
      : user.role === 'admins'
        ? 'Admin'
        : 'Cliente'
    : null;

  // Subtle tint when logged in so the user knows they're inside the app shell.
  const headerTint = user
    ? 'border-accent/30 bg-accent/[0.03] backdrop-blur supports-[backdrop-filter]:bg-accent/[0.04]'
    : 'border-border/60 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60';

  return (
    <header className={cn('sticky top-0 z-40 w-full border-b transition-colors', headerTint)}>
      <div className="container-px mx-auto flex h-16 max-w-7xl items-center justify-between gap-4">
        <Link to={roleHomePath} className="focus-ring rounded-sm flex items-center gap-3">
          <Logo />
          {user && (
            <span className="hidden sm:inline-block text-[11px] font-medium uppercase tracking-wide text-accent">
              Área {roleLabel?.toLowerCase()}
            </span>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                cn(
                  'text-sm transition-colors hover:text-foreground',
                  isActive ? 'text-foreground' : 'text-muted-foreground',
                )
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className={cn(
                  'inline-flex items-center gap-2.5 rounded-full border border-border/60 pl-1.5 pr-3 py-1 transition-colors',
                  'hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  menuOpen && 'bg-muted/40',
                )}
                aria-expanded={menuOpen}
                aria-haspopup="menu"
              >
                <span
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent/20 text-[11px] font-semibold text-accent"
                  aria-hidden
                >
                  {getInitials(user.name || user.email)}
                </span>
                <span className="hidden sm:flex flex-col items-start leading-tight">
                  <span className="text-xs font-medium text-foreground">
                    {user.name?.split(' ')[0] || user.email.split('@')[0]}
                  </span>
                  {roleLabel && (
                    <Badge
                      variant={
                        user.role === 'admins' ? 'danger' : user.role === 'lawyers' ? 'accent' : 'muted'
                      }
                      className="text-[9px] px-1.5 py-0 leading-tight h-3.5"
                    >
                      {roleLabel}
                    </Badge>
                  )}
                </span>
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-56 rounded-md border border-border/80 bg-card shadow-lg p-1.5 text-sm"
                >
                  <div className="px-3 py-2 border-b border-border/60">
                    <p className="font-medium truncate">{user.name || user.email.split('@')[0]}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <Link
                    to={roleHomePath}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 rounded hover:bg-muted/60"
                    role="menuitem"
                  >
                    <UserCircle className="h-4 w-4 text-muted-foreground" /> Minha área
                  </Link>
                  {user.role === 'lawyers' && (
                    <Link
                      to="/lawyer/perfil"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded hover:bg-muted/60"
                      role="menuitem"
                    >
                      <Settings className="h-4 w-4 text-muted-foreground" /> Perfil
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded hover:bg-muted/60 text-danger"
                    role="menuitem"
                  >
                    <LogOut className="h-4 w-4" /> Sair
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm">
                <Link to="/login">Entrar</Link>
              </Button>
              <Button asChild variant="accent" size="sm">
                <Link to="/cadastro">Começar grátis</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
